import { Toast } from '@loophealth/loop-ui-web-library';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMember, getModularUpdatedFamilyStructure } from '../../../../adapters';
import {
    fetchModularFamily,
    setModularSelectedAddOns,
    setModularUpgradedFamilyStructure
} from '../../../../redux/slices/ModularEnrolmentSlice';
import {
    selectModularAddOnBenefits,
    selectModularFamily,
    selectModularPlans,
    selectModularPolicyId,
    selectModularSelectedAddOns,
    selectModularSelectedBasePlan,
    selectModularUpgradedFamilyStructure
} from '../../../../redux/slices/ModularEnrolmentSlice/selectors';
import { IAlacarte } from '../../../../redux/slices/PolicyListSlice/types';
import { formatAmount } from '../../../../utils/common';
import {
    getFamilyDefinitionDescription,
    getIneligibleFamilyMembers,
    isBetterFamilyStructure
} from '../../../../utils/mapping';
import { selectUserData } from '../../../pages/LoginContainer/redux/slices/UserSlice/selectors';
import useModulerEnrolmentContext from '../../../pages/SPAContainer/pages/ModularEnrolmentPage/ModulerEnrolmentContext';
import useModularEnrolment from '../../ModularEnrolmentFooter/hooks/useModularEnrolment';
import usePremiumBreakdown from '../../ModularEnrolmentFooter/hooks/usePremiumBreakdown';
import { IFamilyUpgradeDiff, IPlanDeletionDiff, IPlanDeletionDiffMode, IUseModularAddOns } from '../types';

const useModularAddOns = (runEffects?: boolean): IUseModularAddOns => {
    const toast = Toast.useToast();
    const dispatch = useDispatch();
    const [isVisible, setIsVisible] = React.useState(false);
    const [isCompleteModalVisible, setIsCompleteModalVisible] = React.useState(false);
    const [isDeletionModalVisible, setIsDeletionModalVisible] = React.useState(false);
    const [deletionPlan, setDeletionPlan] = React.useState<IPlanDeletionDiff>();
    const [famDefUpgrade, setFamDefUpgrade] = React.useState<IFamilyUpgradeDiff>();
    const [readMorePlan, setReadMorePlan] = React.useState<IAlacarte>();
    const [isUpgrading, setIsUpgrading] = React.useState(false);
    const [isDowngrading, setIsDowngrading] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const modularAddOns = useSelector(selectModularAddOnBenefits);
    const modularPlans = useSelector(selectModularPlans).data || [];
    const userId = useSelector(selectUserData)?.data?.userId ?? '';
    const policyId = useSelector(selectModularPolicyId);
    const selectedModularPlanId = useSelector(selectModularSelectedBasePlan);
    const selectedAddOns = useSelector(selectModularSelectedAddOns);
    const upgradedFamilyStructure = useSelector(selectModularUpgradedFamilyStructure);
    const userData = useSelector(selectUserData);
    const selectedPolicyId = useSelector(selectModularPolicyId);
    const modularFamily = useSelector(selectModularFamily);
    const { addOnsModalShown, setAddOnsModalShown, setLastFamilyStructure } = useModulerEnrolmentContext();
    const { updateSelectedBenefits } = useModularEnrolment();
    const { priceSummary } = usePremiumBreakdown();
    const addOns = modularAddOns?.data || [];

    const onModalClose = () => {
        setIsVisible(false);
        setAddOnsModalShown(true);
    };

    const getUpdatedFamilyStructure = async (addId?: string, removeId?: string) => {
        let currentSelectedAddOns = addId ? [...selectedAddOns, addId] : [...selectedAddOns];
        if (removeId) {
            currentSelectedAddOns = currentSelectedAddOns.filter((id) => id !== removeId);
        }
        if (currentSelectedAddOns.length === 0) {
            return modularPlans.find((p) => p.id === selectedModularPlanId)?.familyDefinition ?? '';
        }
        const response = await getModularUpdatedFamilyStructure(
            userId,
            policyId,
            selectedModularPlanId ?? '',
            currentSelectedAddOns
        );
        return response.upgradedStructure;
    };

    const handleSelect = async (id: string) => {
        setIsVisible(false);
        setIsUpgrading(true);
        setIsCompleteModalVisible(true);
        try {
            const prev = upgradedFamilyStructure;
            const upgradedStructure = await getUpdatedFamilyStructure(id);
            const updatedAddOns = [...selectedAddOns, id];
            dispatch(setModularUpgradedFamilyStructure(upgradedStructure));
            dispatch(setModularSelectedAddOns(updatedAddOns));
            setFamDefUpgrade({
                prev: getFamilyDefinitionDescription(prev ?? ''),
                new: getFamilyDefinitionDescription(upgradedStructure),
                prevOrg: prev ?? ''
            });
            await updateSelectedBenefits({ addOns: updatedAddOns });
        } catch (e) {
            toast?.error('Failed to upgrade family structure. Please try again.');
            setIsCompleteModalVisible(false);
            setIsVisible(true);
        }
        setIsUpgrading(false);
    };

    const handleUpdate = async (prev: string, curr: string) => {
        if (!prev) return handleSelect(curr);
        if (prev === curr) return;
        setIsVisible(false);
        setIsUpgrading(true);
        setIsCompleteModalVisible(true);
        const lastStructure = upgradedFamilyStructure;
        try {
            const updatedStructure = await getUpdatedFamilyStructure(curr, prev);
            const ineligibleDependents = getIneligibleFamilyMembers(updatedStructure, modularFamily.data ?? []);
            if (ineligibleDependents.length > 0) {
                let prevPlan: IAlacarte = addOns[0];
                let currPlan: IAlacarte = addOns[0];
                addOns.forEach((a) => {
                    const plans = a.subCategories.length
                        ? a.subCategories.map((s) => ({ ...a, ...s, name: a.name }))
                        : [a];
                    plans.forEach((s) => {
                        if (s.id === prev) prevPlan = s;
                        if (s.id === curr) currPlan = s;
                    });
                });
                const newPremium = priceSummary.totalPremium - prevPlan?.price + currPlan?.price;
                setDeletionPlan({
                    mode: IPlanDeletionDiffMode.EDIT,
                    plan: prevPlan,
                    prevPremium: formatAmount(priceSummary.totalPremium),
                    newPremium: formatAmount(newPremium),
                    deletableDependents: ineligibleDependents,
                    downgradedStructure: updatedStructure
                });
                setIsCompleteModalVisible(false);
                setIsUpgrading(false);
                setIsDeletionModalVisible(true);
            } else {
                const isUpgraded = isBetterFamilyStructure(lastStructure ?? '', updatedStructure);
                const updatedAddOns = selectedAddOns.filter((s) => s !== prev).concat(curr);
                if (isUpgraded && lastStructure) {
                    setFamDefUpgrade({
                        prev: getFamilyDefinitionDescription(lastStructure ?? ''),
                        new: getFamilyDefinitionDescription(updatedStructure),
                        prevOrg: lastStructure ?? ''
                    });
                } else {
                    setIsCompleteModalVisible(false);
                }
                dispatch(setModularUpgradedFamilyStructure(updatedStructure));
                dispatch(setModularSelectedAddOns(updatedAddOns));
                await updateSelectedBenefits({ addOns: updatedAddOns });
            }
        } catch (e) {
            toast?.error('Failed to change family structure. Please try again.');
            setIsCompleteModalVisible(false);
            setIsVisible(true);
        }
        setIsUpgrading(false);
    };

    const updatePostDeletionState = async (id: string, newStructure: string, name: string) => {
        const updatedAddOns = selectedAddOns.filter((a) => a !== id);
        dispatch(setModularSelectedAddOns(updatedAddOns));
        dispatch(setModularUpgradedFamilyStructure(newStructure));
        setIsDeletionModalVisible(false);
        setDeletionPlan(undefined);
        await updateSelectedBenefits({ addOns: updatedAddOns });
        toast?.warning(`${name} has been successfully removed.`);
    };

    const handleRemove = async (id: string) => {
        setIsVisible(false);
        setIsDowngrading(true);
        setIsDeletionModalVisible(true);
        let plan: IAlacarte = addOns[0];
        addOns.forEach((a) => {
            if (a.subCategories.length) {
                a.subCategories.forEach((s) => {
                    if (s.id === id) plan = { ...a, ...s, name: `${a.name} - ${s.name}` };
                });
            } else if (a.id === id) plan = a;
        });
        try {
            const downgradedStructure = await getUpdatedFamilyStructure(undefined, id);
            const ineligibleDependents = getIneligibleFamilyMembers(downgradedStructure, modularFamily.data ?? []);
            if (ineligibleDependents.length > 0) {
                setDeletionPlan({
                    mode: IPlanDeletionDiffMode.DEL,
                    plan,
                    prevPremium: formatAmount(priceSummary.totalPremium),
                    newPremium: formatAmount(priceSummary.totalPremium - plan?.price),
                    deletableDependents: ineligibleDependents,
                    downgradedStructure
                });
            } else await updatePostDeletionState(id, downgradedStructure, plan.name);
        } catch (e) {
            toast?.error('Failed to remove selected benefit. Please try again.');
            setIsDeletionModalVisible(false);
            setIsVisible(true);
        }
        setIsDowngrading(false);
    };

    const confirmRemoveDependents = async () => {
        if (!deletionPlan || isDeleting) return;
        setIsDeleting(true);
        const { deletableDependents, plan, downgradedStructure } = deletionPlan;
        try {
            await Promise.all(
                deletableDependents.map((d) => deleteMember(userData.data.userId, selectedPolicyId, d.id))
            );
            dispatch(fetchModularFamily({ userId: userData.data.userId, policyId: selectedPolicyId || '' }));
            await updatePostDeletionState(plan.id, downgradedStructure, plan.name);
        } catch (e) {
            toast?.error('Failed to remove dependents. Please try again.');
        }
        setIsDeleting(false);
    };

    const handleReadMore = (addOn: IAlacarte) => {
        setIsVisible(false);
        setReadMorePlan(addOn);
    };

    const closeReadMoreModal = () => {
        setReadMorePlan(undefined);
        setIsVisible(true);
    };

    const handleCloseCompletionModal = () => {
        if (isUpgrading) return;
        setIsCompleteModalVisible(false);
        setAddOnsModalShown(true);
        toast?.success('Your family structure has been successfully upgraded.');
        setLastFamilyStructure(famDefUpgrade?.prevOrg);
    };

    const handleCloseDeletionModal = () => {
        if (isDeleting) return;
        setDeletionPlan(undefined);
        setIsDeletionModalVisible(false);
        setIsVisible(true);
    };

    useEffect(() => {
        if (!runEffects) return;
        if (selectedAddOns.length === 0 && addOns.length && !addOnsModalShown) {
            setIsVisible(true);
        } else if (selectedAddOns.length) {
            setAddOnsModalShown(true);
        }
    }, [addOnsModalShown, selectedAddOns, addOns]);

    return {
        isVisible,
        setIsVisible,
        selectedAddOns,
        isCompleteModalVisible,
        isDeleting,
        isDeletionModalVisible,
        isUpgrading,
        isDowngrading,
        deletionPlan,
        famDefUpgrade,
        readMorePlan,
        addOns,
        handleUpdate,
        onModalClose,
        handleSelect,
        handleRemove,
        handleReadMore,
        closeReadMoreModal,
        handleCloseCompletionModal,
        handleCloseDeletionModal,
        confirmRemoveDependents
    };
};

export default useModularAddOns;
